import { Link, useParams, Navigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { SERVICES, CASES } from "@/lib/data";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const d = service.detail || {};

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.desc,
    provider: {
      "@type": "Organization",
      name: "LD Media",
      url: "https://ld-media.dk",
      areaServed: { "@type": "Country", name: "Denmark" },
    },
    areaServed: { "@type": "Country", name: "Denmark" },
    serviceType: service.title,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Forside", item: "https://ld-media.dk/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://ld-media.dk/services" },
      { "@type": "ListItem", position: 3, name: service.title, item: `https://ld-media.dk/services/${service.slug}` },
    ],
  };

  const otherServices = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);
  const featuredCase = CASES[0];

  return (
    <>
      <SEO
        title={`${service.title} bureau i Danmark`}
        path={`/services/${service.slug}`}
        description={`${service.title} for danske virksomheder. ${service.desc}`}
        jsonLd={[jsonLd, breadcrumbLd]}
      />

      <PageHero
        eyebrow={service.title}
        title={d.h1 || service.title}
        subtitle={d.lead || service.desc}
      />

      <section className="py-8 lg:py-12 relative">
        <div className="mx-auto max-w-5xl px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-8">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4">Hvad vi tilbyder</h2>
              <p className="text-white/70 leading-relaxed text-base sm:text-lg">
                {d.description || service.desc}
              </p>
            </div>

            {d.forWho && d.forWho.length > 0 && (
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4">Hvem er det for?</h2>
                <ul className="space-y-3">
                  {d.forWho.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-white/75">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {d.benefits && d.benefits.length > 0 && (
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4">Hvad du får</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {d.benefits.map((b) => (
                    <div key={b} className="rounded-2xl border border-white/10 bg-[hsl(var(--bg-card))] p-4 text-sm text-white/80">
                      {b}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-5">
            <Link
              to="/kontakt"
              className="btn-pop block text-center px-6 py-4 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-ink))] font-semibold hover:shadow-[0_12px_40px_-8px_hsla(75,100%,67%,0.7)]"
            >
              Få en gratis analyse →
            </Link>

            <div className="rounded-3xl border border-white/10 bg-[hsl(var(--bg-card))] p-6">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Se også</div>
              <ul className="space-y-2.5">
                {otherServices.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/services/${s.slug}`}
                      className="text-sm text-white/80 hover:text-[hsl(var(--accent))] transition-colors flex items-center justify-between gap-2 group"
                    >
                      <span>{s.title}</span>
                      <span className="text-white/30 group-hover:text-[hsl(var(--accent))]">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {featuredCase && (
              <Link
                to={`/cases/${featuredCase.slug}`}
                className="group block rounded-3xl overflow-hidden border border-white/10 bg-[hsl(var(--bg-card))] hover:border-[hsl(var(--accent))]/60 transition-colors"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={featuredCase.image}
                    alt={`${featuredCase.name} case`}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute bottom-4 left-5 right-5">
                    <div className="text-xs text-[hsl(var(--accent))] uppercase tracking-widest">Featured case</div>
                    <div className="font-display text-lg font-bold mt-1">{featuredCase.name}</div>
                  </div>
                </div>
              </Link>
            )}
          </aside>
        </div>
      </section>

      <CTASection title={`Klar til at komme i gang med ${service.title.toLowerCase()}?`} />
    </>
  );
}
